import { AbstractControl, FormArray, FormControl, ValidatorFn, Validators } from '@angular/forms';

import {
  CombRequest,
  FreestFieldMappingDto,
  FrontEndLimit,
  FrontEndLimitRange,
  UomSystem,
} from './models/chiller-free-st.model';

export interface ValidatableControls {
  evapInletValue: FormControl<number | null>;
  evapOutletTemp: FormControl<number | null>;
  condInletTemp: FormControl<number | null>;
  condOutletVal: FormControl<number | null>;
  evapVarFlowPct?: FormControl<number | null>;
  condVarFlowPct?: FormControl<number | null>;
}

export interface FrontEndLimitOptions {
  seriesId?: string | null;
  evapInletType: string;
  condOutletType: string;
  enabled?: boolean;
}

export const FreeStUtils = {
  getFormatting(mapping: FreestFieldMappingDto | undefined, uomSystem: UomSystem): string | undefined {
    if (!mapping) return undefined;
    const effective = mapping.fixedUomSystem !== 'NONE' ? mapping.fixedUomSystem : uomSystem;
    return effective === 'SI' ? mapping.siFormatting : mapping.impFormatting;
  },

  calcEvapFlowPerKW: (evapInletTemp: number, evapOutletTemp: number): number => {
    return 1 / (4.186 * (evapInletTemp - evapOutletTemp));
  },

  calcEvapInletTemp: (evapOutletTemp: number, evapFlowPerKW: number): number => {
    return evapOutletTemp + 1 / (4.186 * evapFlowPerKW);
  },

  buildCombRequest: (formValues: Record<string, any>, projectName?: string, projectAddress?: string): CombRequest => {
    const isFlowMode = formValues['evapInletType'] === 'EVA_INLET_FLOW';
    const evapOutletTemp: number = formValues['evapOutletTemp'];

    let evapInletTemp: number;
    let evapFlowPerKW: number;

    if (isFlowMode) {
      evapFlowPerKW = formValues['evapInletValue'];
      evapInletTemp = FreeStUtils.calcEvapInletTemp(evapOutletTemp, evapFlowPerKW);
    } else {
      evapInletTemp = formValues['evapInletValue'];
      evapFlowPerKW = FreeStUtils.calcEvapFlowPerKW(evapInletTemp, evapOutletTemp);
    }

    return {
      projectName,
      projectAddress,
      machineSeriesId: formValues['machineSeriesId'],
      compSeriesList: formValues['compSeriesList'],
      lowLift: formValues['lowLift'],
      powerSupplyId: formValues['powerSupplyId'],
      ref: formValues['ref'],
      coolingCapacity: formValues['coolingCapacity'],
      evapInletType: formValues['evapInletType'],
      evapInletTemp,
      evapOutletTemp,
      condInletTemp: formValues['condInletTemp'],
      evapFlowPerKW,
      condOutlet: {
        condOutletType: formValues['condOutletType'],
        condOutletVal: formValues['condOutletVal'],
        definiteTemp: formValues['condOutletType'] === 'COND_OUTLET_TEMP',
      },
      evapVarFlowPct: formValues['evapVarFlowPct'] != null ? formValues['evapVarFlowPct'] / 100 : null,
      condVarFlowPct: formValues['condVarFlowPct'] != null ? formValues['condVarFlowPct'] / 100 : null,
      evapFFId: formValues['evapFFId'],
      condFFId: formValues['condFFId'],
      circuitNum: formValues['circuitNum'],
      cop: formValues['cop'],
      evapPD: formValues['evapPD'],
      condPD: formValues['condPD'],
      selectionMode: formValues['selectionMode'],
      useEcon: formValues['useEcon'],
      standardConditionDesc: formValues['standardConditionDesc'],
      accopRegionId: formValues['accopRegionId'] || 'beijing',
    };
  },

  getLimitRange(frontEndLimit: FrontEndLimit, key: string, seriesId?: string | null): FrontEndLimitRange {
    const entry = frontEndLimit[key];
    if (!entry) return {};
    const defaults = entry.default ?? {};
    const override = seriesId ? (entry[seriesId] ?? {}) : {};
    return {
      min: override.min ?? defaults.min,
      max: override.max ?? defaults.max,
    };
  },

  applyFrontEndLimitValidators(
    controls: ValidatableControls,
    frontEndLimit: FrontEndLimit,
    options: FrontEndLimitOptions
  ): void {
    const { seriesId, evapInletType, condOutletType, enabled = true } = options;
    const range = (key: string) => FreeStUtils.getLimitRange(frontEndLimit, key, seriesId);

    const directFields: { control: FormControl<number | null>; key: string; optional?: boolean }[] = [
      { control: controls.evapOutletTemp, key: 'evapOutlet' },
      { control: controls.condInletTemp, key: 'condInlet' },
    ];
    if (controls.evapVarFlowPct)
      directFields.push({ control: controls.evapVarFlowPct, key: 'evapVarFlowPct', optional: true });
    if (controls.condVarFlowPct)
      directFields.push({ control: controls.condVarFlowPct, key: 'condVarFlowPct', optional: true });

    for (const { control, key, optional } of directFields) {
      const validators: ValidatorFn[] = optional ? [] : [Validators.required];
      if (enabled) {
        const limits = range(key);
        if (limits.min !== undefined) validators.push(Validators.min(limits.min));
        if (limits.max !== undefined) validators.push(Validators.max(limits.max));
      }
      control.setValidators(validators.length ? validators : []);
      control.updateValueAndValidity({ emitEvent: false });
    }

    const evapInletValidators: ValidatorFn[] = [Validators.required];
    if (enabled) {
      if (evapInletType === 'EVA_INLET_FLOW') {
        const limits = range('evapInletFlow');
        if (limits.min !== undefined) evapInletValidators.push(Validators.min(limits.min));
        if (limits.max !== undefined) evapInletValidators.push(Validators.max(limits.max));
      } else {
        const limits = range('evapInOutletTempDiff');
        if (limits.min !== undefined || limits.max !== undefined) {
          evapInletValidators.push(FreeStUtils.tempDiffValidator(controls.evapOutletTemp, limits, true));
        }
      }
    }
    controls.evapInletValue.setValidators(evapInletValidators);
    controls.evapInletValue.updateValueAndValidity({ emitEvent: false });

    const condOutletValidators: ValidatorFn[] = [Validators.required];
    if (enabled) {
      if (condOutletType === 'COND_OUTLET_FLOW') {
        const limits = range('condOutletFlow');
        if (limits.min !== undefined) condOutletValidators.push(Validators.min(limits.min));
        if (limits.max !== undefined) condOutletValidators.push(Validators.max(limits.max));
      } else {
        const limits = range('condInOutletTempDiff');
        if (limits.min !== undefined || limits.max !== undefined) {
          condOutletValidators.push(FreeStUtils.tempDiffValidator(controls.condInletTemp, limits, true));
        }
      }
    }
    controls.condOutletVal.setValidators(condOutletValidators);
    controls.condOutletVal.updateValueAndValidity({ emitEvent: false });
  },

  tempDiffValidator(
    otherControl: FormControl<number | null>,
    limits: FrontEndLimitRange,
    inletMinusOutlet: boolean
  ): ValidatorFn {
    return (control: AbstractControl) => {
      const val = control.value;
      const other = otherControl.value;
      if (val === null || other === null) return null;
      const diff = inletMinusOutlet ? val - other : other - val;
      if ((limits.min !== undefined && diff < limits.min) || (limits.max !== undefined && diff > limits.max)) {
        return { tempDiff: { min: limits.min, max: limits.max, actual: diff } };
      }
      return null;
    };
  },

  loadPctsValidator(control: AbstractControl): { [key: string]: any } | null {
    const arr = control as FormArray;
    const values = arr.controls.map(c => c.value as number);

    for (let i = 1; i < values.length; i++) {
      if (values[i] != null && values[i - 1] != null && values[i] >= values[i - 1]) {
        return { notDescending: { index: i } };
      }
    }

    return null;
  },
};
